import React from 'react';
import { Bell, BellOff } from 'lucide-react';
import { avatarColor, initials } from '@/components/tasks/taskShared';
import { chatActions, useChatStore } from './chatStore';

// Temas: se administran desde Chat → Configuración (store.topics)
export const topicLabel = (topics, k) => topics.find((t) => t.key === k)?.label || 'Otro';

export const STATUSES = [
  { key: 'new', label: 'Nuevo', dot: 'bg-slate-600', chip: 'bg-slate-100 text-slate-700' },
  { key: 'attending', label: 'En atención', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800' },
  { key: 'finalize', label: 'Finalizar', dot: 'bg-indigo-600', chip: 'bg-indigo-50 text-indigo-800', hint: 'Pendiente de aprobación' },
  { key: 'resolved', label: 'Resuelto', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800' },
];
export const statusOf = (k) => STATUSES.find((s) => s.key === k) || STATUSES[0];

// Nombre de un autor/participante a partir del store (equipo o 'tenant:<id>')
export const personName = (s, id, fallback) => {
  if (!id) return fallback || 'Sin asignar';
  if (id === s.meId) return 'Tú';
  if (id.startsWith('tenant:')) return s.tenants.find((t) => t.id === id.slice(7))?.name || fallback || 'Inquilino';
  return s.members.find((u) => u.id === id)?.name || s.employees.find((e) => e.id === id)?.name || fallback || 'Usuario';
};
export const employeeName = (s, id) => s.employees.find((e) => e.id === id)?.name || 'Sin asignar';
export const isTenantId = (id) => !!id && id.startsWith('tenant:');

export const timeLabel = (iso) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
export const dayLabelOf = (iso) => {
  const d = new Date(iso); const now = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, now)) return 'Hoy';
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (same(d, y)) return 'Ayer';
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
};
export const ago = (iso) => {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 36e5);
  if (h < 1) return 'hace minutos';
  if (h < 24) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
};

// Último mensaje del inquilino sin respuesta del equipo → "Sin respuesta · 1 d" (usa mensajes cargados o las marcas de la conversación)
export const unanswered = (messages, conv) => {
  if (messages && messages.length) {
    const last = messages[messages.length - 1];
    return isTenantId(last.author) ? ago(last.ts) : null;
  }
  if (conv?.last_tenant_message_at && (!conv.last_team_message_at || conv.last_tenant_message_at > conv.last_team_message_at)) return ago(conv.last_tenant_message_at);
  return null;
};

export const Avatar = ({ name, className = 'h-9 w-9 text-xs' }) => (
  <span className={`${className} inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white ${avatarColor(name)}`}>{initials(name)}</span>
);

export const SectionLabel = ({ children, action, testId }) => (
  <div className="flex items-center justify-between px-3 pb-1 pt-3">
    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{children}</span>
    {action && <button type="button" onClick={action} className="rounded p-0.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900" aria-label="Agregar" data-testid={testId}>+</button>}
  </div>
);

// "Fulano está escribiendo…" (eventos typing del WebSocket, caducan a los 4 s)
export const TypingBar = ({ convId }) => {
  const s = useChatStore(); const now = Date.now();
  const names = Object.values(s.typing[convId] || {}).filter((t) => t.until > now).map((t) => t.name);
  if (!names.length) return null;
  return <div className="flex items-center gap-1.5 px-5 pb-1 text-xs italic text-slate-500" data-testid="typing-indicator"><span className="inline-flex gap-0.5"><span className="h-1 w-1 animate-bounce rounded-full bg-slate-400" /><span className="h-1 w-1 animate-bounce rounded-full bg-slate-400 [animation-delay:120ms]" /><span className="h-1 w-1 animate-bounce rounded-full bg-slate-400 [animation-delay:240ms]" /></span>{names.join(', ')} {names.length > 1 ? 'están' : 'está'} escribiendo…</div>;
};

// Silenciar / activar el sonido de mensaje nuevo (se recuerda en este navegador)
export const SoundToggle = () => {
  const s = useChatStore();
  return <button type="button" onClick={chatActions.toggleMute} className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition ${s.muted ? 'border-slate-200 text-slate-400 hover:bg-slate-50' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`} title={s.muted ? 'Sonido silenciado · clic para activar' : 'Sonido activado · clic para silenciar'} aria-label={s.muted ? 'Activar sonido' : 'Silenciar sonido'} data-testid="chat-sound-toggle" data-muted={s.muted ? 'true' : 'false'}>{s.muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}</button>;
};

// Estado de la conexión en tiempo real
export const LiveDot = () => {
  const s = useChatStore();
  return <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.live ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} title={s.live ? 'Conectado en tiempo real' : 'Sin conexión en vivo: se actualiza cada 5 s'} data-testid="chat-live-status" data-live={s.live ? 'true' : 'false'}><span className={`h-1.5 w-1.5 rounded-full ${s.live ? 'bg-emerald-500' : 'animate-pulse bg-amber-500'}`} />{s.live ? 'En vivo' : 'Reconectando'}</span>;
};

// Resalta @menciones dentro del texto
// Menciones (@Nombre / @todos) resaltadas y URLs como enlaces; los enlaces largos se parten para no salirse de la burbuja
const TOKEN_RE = /(@[\wÁÉÍÓÚáéíóúñÑ]+(?:\s[A-ZÁÉÍÓÚ][\wáéíóúñ]+)?|https?:\/\/[^\s<>"']+)/g;
export const renderText = (text = '') => text.split(TOKEN_RE).map((part, i) => {
  if (!part) return null;
  if (part.startsWith('@')) return <span key={i} className={`rounded px-1 font-semibold ${part.toLowerCase() === '@todos' ? 'bg-orange-100 text-orange-800' : 'bg-sky-100 text-sky-800'}`}>{part}</span>;
  if (/^https?:\/\//.test(part)) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="break-all underline underline-offset-2 opacity-90 hover:opacity-100" data-testid="chat-link">{part}</a>;
  return <React.Fragment key={i}>{part}</React.Fragment>;
});

// "Unidad | Nombre" — identificador de inquilino en listas y cabeceras
export const UnitName = ({ tenant, className = 'text-sm', testId }) => (
  <span className={`flex min-w-0 items-center gap-1.5 ${className}`} data-testid={testId}>
    <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-bold text-slate-700 ring-1 ring-slate-200">{tenant?.unit || 'S/U'}</span>
    <span className="text-slate-300">|</span>
    <span className="truncate font-bold text-slate-900">{tenant?.name}</span>
  </span>
);
